/**
 * Infinite Scroll for Mobile and Desktop
 * Automatically loads more products when user scrolls near bottom
 */
class InfiniteScroll {
	constructor() {
		this.isMobile = window.innerWidth < 750;
		this.isLoading = false;
		this.hasMorePages = false;
		this.nextPageUrl = null;
		this.productGrid = null;
		this.paginationWrapper = null;
		this.loadingIndicator = null;
		this.scrollListenerAttached = false;

		this.init();
	}

	init() {
		// Run on both mobile and desktop

		// Try to find the product grid - it's either a UL with id="product-grid" or a UL inside a div with that id
		let gridElement = document.querySelector('#product-grid');
		if (gridElement && gridElement.tagName === 'UL') {
			this.productGrid = gridElement;
		} else if (gridElement) {
			// It's a div, find the UL inside
			this.productGrid = gridElement.querySelector('ul.product-grid');
		}

		this.paginationWrapper = document.querySelector('.pagination-wrapper');


		if (!this.productGrid) {
			console.warn('[InfiniteScroll] Product grid UL not found');
			return;
		}

		this.setupPagination();
		this.createLoadingIndicator();

		if (!this.scrollListenerAttached) {
			this.attachScrollListener();
			this.scrollListenerAttached = true;
		}

		// Re-check on resize
		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile;
			this.isMobile = window.innerWidth < 750;

			// If switching from desktop to mobile, reinitialize
			if (!wasMobile && this.isMobile) {
				this.setupPagination();
				if (!this.scrollListenerAttached) {
					this.attachScrollListener();
					this.scrollListenerAttached = true;
				}
			}
		});
	}

	setupPagination() {
		if (!this.paginationWrapper) {
			this.hasMorePages = false;
			return;
		}

		const nextButton = this.paginationWrapper.querySelector('.pagination__item--prev');


		if (nextButton && nextButton.href) {
			this.hasMorePages = true;
			this.nextPageUrl = nextButton.href;

			// Hide pagination on both mobile and desktop
			this.paginationWrapper.style.display = 'none';

		} else {
			this.hasMorePages = false;
		}
	}

	createLoadingIndicator() {
		this.loadingIndicator = document.createElement('div');
		this.loadingIndicator.className = 'infinite-scroll-loading';
		this.loadingIndicator.innerHTML = `
      <div class="loading-spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
		this.loadingIndicator.style.display = 'none';

		const productGridContainer = document.querySelector('#ProductGridContainer, .product-grid-container');
		if (productGridContainer) {
			productGridContainer.appendChild(this.loadingIndicator);
		}
	}

	attachScrollListener() {
		// Attach scroll listener for both mobile and desktop
		const scrollHandler = () => {
			if (this.isLoading || !this.hasMorePages || !this.productGrid) {
				return;
			}

			// Get all product items
			const allProducts = Array.from(this.productGrid.querySelectorAll('li'));
			if (allProducts.length === 0) {
				return;
			}

			// Find products in the second-to-last row
			// Group products by their top position to identify rows
			const productsByRow = new Map();
			allProducts.forEach(product => {
				const rect = product.getBoundingClientRect();
				const topPosition = Math.round(rect.top);

				if (!productsByRow.has(topPosition)) {
					productsByRow.set(topPosition, []);
				}
				productsByRow.get(topPosition).push(product);
			});

			// Get all unique row positions sorted
			const rowPositions = Array.from(productsByRow.keys()).sort((a, b) => a - b);

			if (rowPositions.length < 2) {
				// If there's only one row, use the last product
				const lastProduct = allProducts[allProducts.length - 1];
				const lastProductRect = lastProduct.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				const isLastProductVisible = lastProductRect.top < viewportHeight + 100;

				if (isLastProductVisible) {
					this.loadMoreProducts();
				}
				return;
			}

			// Get the second-to-last row position
			const secondToLastRowPosition = rowPositions[rowPositions.length - 2];
			const secondToLastRowProducts = productsByRow.get(secondToLastRowPosition);

			if (!secondToLastRowProducts || secondToLastRowProducts.length === 0) {
				return;
			}

			// Check if any product in the second-to-last row is visible
			const viewportHeight = window.innerHeight;
			const isSecondToLastRowVisible = secondToLastRowProducts.some(product => {
				const rect = product.getBoundingClientRect();
				return rect.top < viewportHeight + 100;
			});

			if (isSecondToLastRowVisible) {
				this.loadMoreProducts();
			}
		};

		// Throttle scroll event
		let ticking = false;
		this.scrollListener = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					scrollHandler();
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener('scroll', this.scrollListener);
	}

	async loadMoreProducts() {
		if (this.isLoading || !this.hasMorePages || !this.nextPageUrl) {
			return;
		}

		this.isLoading = true;
		this.showLoadingIndicator();

		try {
			const response = await fetch(this.nextPageUrl);
			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			// Extract new products - handle both cases (UL is #product-grid or UL inside #product-grid)
			let newProductsGrid = doc.querySelector('#product-grid');
			let newProducts = [];

			if (newProductsGrid && newProductsGrid.tagName === 'UL') {
				newProducts = newProductsGrid.querySelectorAll(':scope > li');
			} else if (newProductsGrid) {
				const ul = newProductsGrid.querySelector('ul.product-grid');
				if (ul) {
					newProducts = ul.querySelectorAll(':scope > li');
				}
			}

			if (newProducts.length > 0) {
				// Append new products to existing grid, filtering out promo cards
				newProducts.forEach(product => {
					// Skip promo cards - they should only appear on the first page
					const isPromoCard = product.querySelector('.promo-card-v2');
					if (!isPromoCard) {
						this.productGrid.appendChild(product.cloneNode(true));
					}
				});

				// Re-initialize custom card buttons if they exist
				if (typeof addButtonsEevents === 'function') {
					addButtonsEevents();
				}

				// Initialize Judge.me ratings for new products
				this.initializeJudgeMeRatings();

				// Dispatch event to reinitialize marquee animations
				document.dispatchEvent(new CustomEvent('infinite-scroll:loaded'));

				// Update pagination info
				const newPaginationWrapper = doc.querySelector('.pagination-wrapper');
				if (newPaginationWrapper) {
					const newNextButton = newPaginationWrapper.querySelector('.pagination__item--prev');

					if (newNextButton && newNextButton.href) {
						this.nextPageUrl = newNextButton.href;
						this.hasMorePages = true;
					} else {
						this.hasMorePages = false;
						this.nextPageUrl = null;
					}

					// Update the hidden pagination wrapper for facets compatibility
					if (this.paginationWrapper) {
						this.paginationWrapper.dataset.page = newPaginationWrapper.dataset.page;
					}
				} else {
					this.hasMorePages = false;
				}

				// Trigger animation if enabled
				if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
					const newItems = Array.from(newProducts);
					newItems.forEach((item, index) => {
						setTimeout(() => {
							const addedItem = this.productGrid.children[this.productGrid.children.length - newProducts.length + index];
							if (addedItem) {
								addedItem.classList.add('animate--slide-in');
							}
						}, index * 50);
					});
				}
			} else {
				this.hasMorePages = false;
				console.warn('[InfiniteScroll] No products found in response');
			}
		} catch (error) {
			console.error('[InfiniteScroll] Error loading more products:', error);
			this.hasMorePages = false;
		} finally {
			this.isLoading = false;
			this.hideLoadingIndicator();
		}
	}

	showLoadingIndicator() {
		if (this.loadingIndicator) {
			this.loadingIndicator.style.display = 'flex';
		}
	}

	hideLoadingIndicator() {
		if (this.loadingIndicator) {
			this.loadingIndicator.style.display = 'none';
		}
	}

	// Initialize Judge.me ratings for newly loaded products
	initializeJudgeMeRatings() {
		if (!this.productGrid) return;

		const shopDomain = window.Shopify?.shop || '';
		const ratingContainers = this.productGrid.querySelectorAll('.custom-product-card__rating');

		ratingContainers.forEach(ratingContainer => {
			// Skip if already initialized
			if (ratingContainer.dataset.ratingInitialized === 'true') return;

			const productId = ratingContainer.dataset.productId;
			const productHandle = ratingContainer.dataset.handle;

			if (!productId || !productHandle) return;

			// Mark as initialized to prevent duplicate fetches
			ratingContainer.dataset.ratingInitialized = 'true';

			// Fetch and update rating
			this.fetchJudgeMeRating(ratingContainer, shopDomain, productId, productHandle);
		});
	}

	// Fetch Judge.me rating for a single product
	fetchJudgeMeRating(ratingContainer, shopDomain, productId, productHandle) {
		const urls = [
			`https://judge.me/api/v1/widgets/product_review?api_token=xo3qJH3iOek5VTGO32yvZuXdCic&shop_domain=${shopDomain}&platform=shopify&handle=${productHandle}`,
			`https://judge.me/api/v1/widgets/product_review?api_token=xo3qJH3iOek5VTGO32yvZuXdCic&shop_domain=${shopDomain}&platform=shopify&id=${productId}`
		];

		let currentUrlIndex = 0;

		const tryFetch = () => {
			if (currentUrlIndex >= urls.length) {
				ratingContainer.style.display = 'none';
				return;
			}

			fetch(urls[currentUrlIndex])
				.then(res => res.json())
				.then(data => {
					if (data && data.error) {
						currentUrlIndex++;
						tryFetch();
						return;
					}

					if (data && data.rating) {
						this.updateStars(data.rating, ratingContainer);
					} else if (data && data.widget) {
						// Parse HTML widget to extract rating
						const tempDiv = document.createElement('div');
						tempDiv.innerHTML = data.widget;
						const starsElement = tempDiv.querySelector('[aria-label*="star"]');
						if (starsElement) {
							const ariaLabel = starsElement.getAttribute('aria-label');
							const ratingMatch = ariaLabel.match(/(\d+\.?\d*)\s*stars?/i);
							if (ratingMatch) {
								this.updateStars(parseFloat(ratingMatch[1]), ratingContainer);
							} else {
								ratingContainer.style.display = 'none';
							}
						} else {
							ratingContainer.style.display = 'none';
						}
					} else {
						ratingContainer.style.display = 'none';
					}
				})
				.catch(err => {
					console.log('Error fetching Judge.me data:', err);
					currentUrlIndex++;
					tryFetch();
				});
		};

		tryFetch();
	}

	// Update star display based on rating
	updateStars(rating, ratingContainer) {
		if (!rating || rating === 0) {
			ratingContainer.style.display = 'none';
			return;
		}

		const stars = ratingContainer.querySelectorAll('.rating-stars path');
		const fullStars = Math.floor(rating);
		const hasHalfStar = (rating % 1) >= 0.5;

		stars.forEach((star, index) => {
			if (index < fullStars) {
				// Full star
				star.setAttribute('fill', '#443C31');
				star.style.opacity = '1';
			} else if (index === fullStars && hasHalfStar) {
				// Half star
				star.setAttribute('fill', '#443C31');
				star.style.opacity = '0.5';
			} else {
				// Empty star
				star.setAttribute('fill', '#cccccc');
				star.style.opacity = '1';
			}
		});
	}

	// Method to reinitialize after filter changes
	reinitialize() {
		// Reinitialize for both mobile and desktop
		let gridElement = document.querySelector('#product-grid');
		if (gridElement && gridElement.tagName === 'UL') {
			this.productGrid = gridElement;
		} else if (gridElement) {
			// It's a div, find the UL inside
			this.productGrid = gridElement.querySelector('ul.product-grid');
		}

		this.paginationWrapper = document.querySelector('.pagination-wrapper');


		if (this.paginationWrapper) {
			this.setupPagination();
		} else {
			this.hasMorePages = false;
		}

		// Reset loading state
		this.isLoading = false;
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		window.infiniteScroll = new InfiniteScroll();
	});
} else {
	window.infiniteScroll = new InfiniteScroll();
}

// Reinitialize after facet filter updates
document.addEventListener('facets:updated', () => {
	if (window.infiniteScroll) {
		setTimeout(() => {
			window.infiniteScroll.reinitialize();
		}, 100);
	}
});
